import { isAppUpdateDirty } from "@/domains/shared/appUpdatePrompt/isAppUpdateDirty";
import {
  setAppUpdatePerformReload,
  useAppUpdatePromptStore,
} from "@/domains/shared/appUpdatePrompt/appUpdatePromptStore";
import { registerSW } from "virtual:pwa-register";

export const SERVICE_WORKER_UPDATE_INTERVAL_MS = 60 * 60 * 1000;

let refreshing = false;

/**
 * When the SW is about to take control: hard path if clean; defer if dirty (FR-03, BR-06, BR-07).
 * Replaces silent location.reload().
 */
export function installServiceWorkerControllerReload(): void {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) {
      return;
    }

    useAppUpdatePromptStore.getState().markUpdateKnown();

    if (useAppUpdatePromptStore.getState().hardActive) {
      return;
    }

    if (isAppUpdateDirty()) {
      // BR-07 / E-05 / E-13: defer hard force-reload; soft path remains available.
      return;
    }

    // BR-06 / E-06 / AC-05: prompted hard path — no silent surprise reload.
    refreshing = true;
    useAppUpdatePromptStore.getState().enterHardPath();
  });
}

export function scheduleServiceWorkerUpdateChecks(
  registration: ServiceWorkerRegistration,
  intervalMs = SERVICE_WORKER_UPDATE_INTERVAL_MS,
): () => void {
  const checkForUpdates = () => {
    if (!navigator.onLine) {
      // BR-11 / E-08: no soft nag from failed/offline update checks.
      return;
    }

    // Network blips reject update(); navigator.onLine is unreliable. Swallow so
    // Sentry does not treat transient SW script fetch failures as app errors.
    void registration.update().catch(() => undefined);
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      checkForUpdates();
    }
  };

  document.addEventListener("visibilitychange", onVisibilityChange);
  const intervalId = window.setInterval(checkForUpdates, intervalMs);

  return () => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.clearInterval(intervalId);
  };
}

export function registerAppServiceWorker(): void {
  if (!import.meta.env.PROD) {
    return;
  }

  installServiceWorkerControllerReload();

  registerSW({
    immediate: true,
    onNeedRefresh() {
      // FR-01 / BR-15: mark update known only — soft UI waits for later soft triggers.
      useAppUpdatePromptStore.getState().markUpdateKnown();
    },
    onRegisteredSW(_swUrl, registration) {
      if (!registration) {
        return;
      }

      // BR-16: hourly + visibility update checks unchanged.
      scheduleServiceWorkerUpdateChecks(registration);
    },
  });

  // BR-10 / BR-17: under autoUpdate, updateSW(true) does not reload the document.
  setAppUpdatePerformReload(() => {
    refreshing = true;
    window.location.reload();
  });
}
